import {AfterContentChecked, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {SecondFactorAuthPageService} from "./second-factor-auth-page.service";
import {Router} from "@angular/router";
import {DisplayTextModel} from "@syncfusion/ej2-angular-barcode-generator";
import {$translate} from "../../core/services/internationalization/localize.service";
import {MaskChangeEventArgs, MaskedTextBox} from "@syncfusion/ej2-angular-inputs";
import {AlertService} from "../../core/services/alert/alert.service";

@Component({
  selector: 'sb-second-factor-auth-page',
  templateUrl: './second-factor-auth-page.component.html',
  styleUrls: ['./second-factor-auth-page.component.scss']
})
export class SecondFactorAuthPageComponent implements OnInit, AfterContentChecked {

  @ViewChild("authCodeInput")
  authCodeInput: MaskedTextBox

  factorId: string;
  rememberMe: boolean = false;

  isVerifyButtonEnabled: boolean = false;
  verificationCodeMode: boolean = false;
  registrationPhase: boolean = false;

  showBackButton: boolean = false;
  showInstructionTooltip: boolean = false;
  showSetupSuccessButton: boolean = false;
  showNextButton: boolean = false;
  isRememberMeCheckBoxVisible: boolean;

  // TOTP
  loadQRCode: boolean = false;
  factorURI: string;

  factorQRCodeDisplayTextModel: DisplayTextModel = {
    font: 'Poppins',
    size: 14,
    alignment: 'Center',
  };

  hasErrors: boolean;
  errorMessage: string;

  instructions: string;
  qrCodeSetupTooltipContent: string;
  subTitle: string;

  pageTitle: string = $translate('ng.page.2fa.pageTitle')`Second Factor Authentication`;
  authenticationCodeInputPlaceholder: string = $translate('ng.page.2fa.code.input.placeholder')`E.g 123456`;

  maxAttemptsReachedTitle = $translate('ng.page.2fa.maxAttemptsReachedTitle')`Maximum Attempts Reached`;
  maxAttemptsReachedMessage = $translate('ng.page.2fa.maxAttemptsReachedMessage')`Redirecting to login page`;
  incorrectCodeMessage: string = $translate('ng.page.2fa.incorrectCodeMessage')`The code you entered is incorrect. Please try again or contact support.`;
  systemError: string = $translate('ng.page.2fa.systemError')`System error. Please contact support`;

  nextButtonLabel: string = $translate('ng.page.2fa.setup.step1.submit.button.label')`Done, next step`;
  verifyCodeButtonLabel: string = $translate('ng.page.2fa.verify.button.label')`Verify`;
  backButtonLabel: string = $translate('ng.page.2fa.setup.back.button.label')`Back`;
  rememberMeCheckboxLabel: string = $translate('login.remember.me')`Remember me`;
  setupSuccessButtonLabel: string = $translate('ng.page.2fa.setup.success.button.label')`Go to Servicely`;

  constructor(private secondFactorAuthPageService: SecondFactorAuthPageService, private router: Router, private alertService: AlertService) {
  }

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  ngAfterContentChecked(): void {
    if (this.authCodeInput && !this.authCodeInput.htmlAttributes['hidden']) {
      this.authCodeInput.focusIn()
    }
  }

  private async load() {
    const factorsInfo = await this.secondFactorAuthPageService.factorsInfo();
    this.isRememberMeCheckBoxVisible = factorsInfo.rememberMeEnabled

    this.isRememberMeCheckBoxVisible = factorsInfo.rememberMeEnabled;

    if (factorsInfo.hasFactors) {
      const currentFactor = factorsInfo.factors[0]
      this.factorId = currentFactor.id;

      this.factorURI = currentFactor?.data?.factorURI
      if (this.factorURI) {
        this.setQRCodeSecret(currentFactor?.data?.factorSecret);
        this.showRegistrationStep1();
      } else {
        this.showCodeVerification();
      }
    } else {
      await this.registerTotpFactor();
    }
  }

  private async registerTotpFactor() {
    try {
      const registration = await this.secondFactorAuthPageService.registerFactor('TOTP');
      this.factorURI = registration.data.factorURI;
      this.factorId = registration.id;

      if (this.factorURI) {
        this.setQRCodeSecret(registration.data.factorSecret);
        this.showRegistrationStep1();
      } else {
        console.debug('No QR code to display');
        return;
      }

    } catch (e) {
      this.showErrorMessage($translate('ng.page.2fa.qrcode.creation.error.message')`Failed to create QR Code`);
      this.verificationCodeMode = false;
      console.error(e);
    }
  }

  private handleChallengeError(errorPayload?:{isSuccess: Boolean, attemptsLeft: number | null}) {
    let errorMessage = this.incorrectCodeMessage
    if (errorPayload && Number.isInteger(errorPayload.attemptsLeft)) {
      if (errorPayload.attemptsLeft > 0) {
        errorMessage = errorMessage + '\n' + $translate('ng.page.2fa.attemptsLeftMessage')`You have ${errorPayload.attemptsLeft} attempts left.`;
      } else {
        this.alertService.errorMessage( this.maxAttemptsReachedMessage, this.maxAttemptsReachedTitle, () => {
          location.href = '/login'
        });
      }
    }

    this.showErrorMessage(errorMessage);

    this.isVerifyButtonEnabled = false;

    this.authCodeInput.value = null
    this.authCodeInput.focusIn();
  }

  async handleVerifyCodeClick(e: any) {
    this.clearErrorMessage();
    const authCode = this.authCodeInput.value;

    if (!authCode || authCode.trim().length != 6) {
      this.showErrorMessage(this.incorrectCodeMessage);
      return;
    }

    if (!this.factorId) {
      this.showErrorMessage(this.systemError);
      return;
    }

    const challengeValue = authCode.trim();
    try {
      const result = await this.secondFactorAuthPageService.submitChallenge(this.factorId, challengeValue, this.rememberMe);
      if (result && result.isSuccess === false) {
        this.handleChallengeError(result);
        return;
      }
    } catch (e) {
      this.handleChallengeError();
      return;
    }

    if (this.registrationPhase === true) {
      this.showRegistrationDone();
    } else {
      await this.router.navigateByUrl("/");
    }
  }

  async handleRegistrationSuccessClick(e: any) {
    await this.router.navigateByUrl("/");
  }

  private setQRCodeSecret(secret: string) {
    this.factorQRCodeDisplayTextModel = {
      ...this.factorQRCodeDisplayTextModel,
      text: secret,
      visibility: secret !== null && secret !== 'undefined'
    };
  }

  private showErrorMessage(message: string) {
    this.hasErrors = true;
    this.errorMessage = message;
  }

  private clearErrorMessage() {
    this.hasErrors = false;
    this.errorMessage = '';
  }

  handleNextStepClick($event: MouseEvent) {
    this.showRegistrationStep2();
  }

  handleBackClick($event: MouseEvent) {
    this.showRegistrationStep1();
  }

  showCodeVerification() {
    this.verificationCodeMode = true;
    this.instructions = $translate('ng.page.2fa.totp.setup.verifyCodeInstructions')`Enter the code generated by your authenticator.`;
  }

  showRegistrationStep1() {
    this.registrationPhase = true;
    const registrationStep = 1;
    this.verificationCodeMode = false;
    this.loadQRCode = true;
    this.showBackButton = false;
    this.showNextButton = true;
    this.instructions = $translate('ng.page.2fa.totp.setup.qrCodeInstructions')`We’re adding two-factor authentication (2FA). Please scan the QR code in the authenticator app
of your choice OR enter the code below.`;
    this.subTitle= $translate('ng.page.2fa.setup.title')`Setup - Step ${(registrationStep)} of 2`;
    this.showInstructionTooltip = true;
    this.qrCodeSetupTooltipContent = $translate('ng.page.2fa.setup.qrCodeInstructions.tooltip.content')`Google Authenticator, Microsoft Authenticator, Twilio Authy or Duo Mobile can be downloaded from Google Play or the Apple App Store.`;
  }

  showRegistrationStep2() {
    const registrationStep = 2;
    this.showNextButton = false;
    this.verificationCodeMode = true;
    this.loadQRCode = false;
    this.showBackButton = true;
    this.showInstructionTooltip = false;
    this.instructions = $translate('ng.page.2fa.totp.setup.verifyCodeInstructions')`Enter the code generated by your authenticator app to complete verification.`;
    this.subTitle= $translate('ng.page.2fa.setup.title')`Setup - Step ${(registrationStep)} of 2`;
  }

  showRegistrationDone() {
    this.subTitle = null;
    this.showBackButton = false;
    this.showSetupSuccessButton = true;
    this.verificationCodeMode = false;
    this.instructions = $translate('ng.page.2fa.totp.setup.success.message')`You're all set.`;
  }

  public onAuthInputCodeChange(event: MaskChangeEventArgs): void {
    if (event.value && event.value.length > 0) {
      this.clearErrorMessage();
    }
    this.isVerifyButtonEnabled = event.value?.length === 6;
  }
}
